import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Heart, Lock, Download, Flag, ShieldCheck, Star, Calendar, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { FormatBadge, Stars, Spinner, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, media, naira, fmtDate, errMsg, downloadTemplate } from "@/lib/api";

const REASONS = ["Copyright issue", "Fraud", "Incorrect description", "Inappropriate content", "Other"];

function ReportDialog({ tid }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const submit = async () => {
    try { await api.post(`/templates/${tid}/report`, { reason, details }); toast.success("Report submitted. Our team will review it."); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (user ? setOpen(o) : nav("/login"))}>
      <DialogTrigger asChild><button data-testid="report-template-btn" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-rose-500"><Flag size={13} />Report this template</button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Report template</DialogTitle></DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="gap-3">
          {REASONS.map((r) => <div key={r} className="flex items-center gap-2"><RadioGroupItem value={r} id={r} data-testid={`report-reason-${r.split(" ")[0].toLowerCase()}`} /><Label htmlFor={r}>{r}</Label></div>)}
        </RadioGroup>
        <Textarea data-testid="report-details-input" placeholder="Add details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} />
        <Button data-testid="report-submit-btn" onClick={submit}>Submit report</Button>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewForm({ tid, initial, onDone }) {
  const [rating, setRating] = useState(initial?.rating || 5);
  const [comment, setComment] = useState(initial?.comment || "");
  const submit = async () => {
    try { await api.post(`/templates/${tid}/reviews`, { rating, comment }); toast.success("Thanks for your review!"); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="review-form">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}><Star size={24} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} /></button>
        ))}
      </div>
      <Textarea data-testid="review-comment-input" placeholder="What did you think of this template?" value={comment} onChange={(e) => setComment(e.target.value)} />
      <Button data-testid="review-submit-btn" onClick={submit}>{initial ? "Update review" : "Post review"}</Button>
    </div>
  );
}

function Reviews({ t, reload }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/templates/${t.id}/reviews`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [t.id]); // eslint-disable-line
  return (
    <section className="mt-16" data-testid="reviews-section">
      <div className="mb-6 flex items-center gap-4"><h2 className="text-2xl font-bold">Reviews</h2><Stars value={t.rating_avg} count={t.rating_count} size={18} /></div>
      {t.viewer.purchased && <div className="card-surface mb-6 p-5"><p className="mb-3 text-sm font-semibold">{t.viewer.my_review ? "Your review" : "Rate this template"}</p><ReviewForm tid={t.id} initial={t.viewer.my_review} onDone={() => { load(); reload(); }} /></div>}
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No reviews yet. Only verified purchasers can review.</p> : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((r) => (
            <div key={r.id} className="card-surface p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between"><p className="font-semibold">{r.user_name}</p><Stars value={r.rating} /></div>
              <p className="mt-2 text-sm text-muted-foreground">{r.comment}</p>
              <p className="mt-3 inline-flex items-center gap-1 text-xs text-emerald-500"><ShieldCheck size={12} />Verified purchase · {fmtDate(r.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function BuyBox({ t, onFav }) {
  const { user, favIds } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const dl = async () => { setBusy(true); try { const f = await downloadTemplate(t.id); toast.success(`Downloading ${f}`); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  return (
    <div className="card-surface p-6">
      <p className="eyebrow">Price</p>
      <p className="mt-1 font-display text-4xl font-bold" data-testid="template-price">{naira(t.price)}</p>
      <div className="mt-6 grid gap-3">
        {t.viewer.purchased || t.viewer.is_owner ? (
          <Button size="lg" onClick={dl} disabled={busy} data-testid="download-file-btn"><Download size={17} className="mr-2" />Download editable file</Button>
        ) : (
          <Button size="lg" data-testid="buy-now-btn" onClick={() => nav(user ? `/checkout/${t.id}` : `/login?next=/checkout/${t.id}`)}>Buy Now</Button>
        )}
        <Button size="lg" variant="outline" onClick={onFav} data-testid="add-favorite-btn">
          <Heart size={17} className={`mr-2 ${favIds.has(t.id) ? "fill-rose-500 text-rose-500" : ""}`} />{favIds.has(t.id) ? "Saved to Favorites" : "Add to Favorites"}
        </Button>
      </div>
      <div className="mt-5 flex items-start gap-2 rounded-xl bg-secondary p-3 text-xs text-muted-foreground">
        <Lock size={14} className="mt-0.5 shrink-0 text-primary" />
        <span>{t.viewer.purchased ? "You own this template. Download it anytime from My Purchases." : "The original editable file is stored privately and unlocked only after verified payment."}</span>
      </div>
    </div>
  );
}

export default function TemplateDetail() {
  const { id } = useParams();
  const { user, toggleFav } = useAuth();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [active, setActive] = useState(0);
  const load = () => api.get(`/templates/${id}`).then((r) => setT(r.data)).catch(() => setT(false));
  useEffect(() => { load(); setActive(0); }, [id]); // eslint-disable-line
  const onFav = async () => { if (!user) return nav("/login"); try { const on = await toggleFav(id); toast.success(on ? "Saved to favorites" : "Removed from favorites"); } catch (e) { toast.error(errMsg(e)); } };

  if (t === null) return <Spinner />;
  if (t === false) return <div className="py-32 text-center"><h1 className="text-2xl font-bold">Template not found</h1><Link to="/explore" className="text-primary">Back to marketplace</Link></div>;
  const specs = [
    ["File format", t.file_formats.map((f) => <FormatBadge key={f} f={f} className="mr-1" />)],
    ["Compatible with", t.software.join(", ")],
    ["Size", t.dimensions || "—"],
    ["Category", <Link to={`/explore?category=${t.category_slug}`} className="text-primary">{t.category_name}</Link>],
    ["Uploaded", fmtDate(t.created_at)],
    ["File size", t.file ? `${(t.file.size / 1024 / 1024).toFixed(2)} MB (.${t.file.ext})` : "—"],
  ];
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="grid gap-10 lg:grid-cols-[1.35fr_1fr]">
        <div>
          <div className="card-surface overflow-hidden bg-secondary">
            <img src={media(t.images[active])} alt={t.title} className="mx-auto max-h-[75vh] w-full object-contain" data-testid="template-main-image" />
          </div>
          {t.images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {t.images.map((u, i) => (
                <button key={u} onClick={() => setActive(i)} data-testid={`template-thumb-${i}`} className={`h-20 w-20 overflow-hidden rounded-xl border-2 ${i === active ? "border-primary" : "border-transparent"}`}>
                  <img src={media(u)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          {t.status !== "approved" && <StatusBadge s={t.status} testid="template-status" />}
          <div>
            <Link to={`/explore?category=${t.category_slug}`} className="eyebrow hover:text-primary">{t.category_name}</Link>
            <h1 className="mt-2 text-3xl font-bold sm:text-4xl" data-testid="template-title">{t.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <Stars value={t.rating_avg} count={t.rating_count} />
              <span data-testid="template-sales">{t.sales_count} sales</span>
              <span className="inline-flex items-center gap-1"><Eye size={14} />{t.views}</span>
            </div>
            <p className="mt-3 text-sm">By <Link to={`/explore?seller=${t.seller_id}`} className="font-semibold text-primary" data-testid="template-seller-link">{t.seller_name}</Link></p>
          </div>
          <BuyBox t={t} onFav={onFav} />
          <div className="card-surface divide-y divide-border">
            {specs.map(([k, v]) => <div key={k} className="flex items-center justify-between gap-4 px-5 py-3 text-sm"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v}</span></div>)}
          </div>
          <ReportDialog tid={t.id} />
        </div>
      </div>
      <section className="mt-14 max-w-3xl">
        <h2 className="mb-4 text-2xl font-bold">Description</h2>
        <p className="whitespace-pre-line text-muted-foreground" data-testid="template-description">{t.description}</p>
        <div className="mt-5 flex flex-wrap gap-2">{t.tags.map((g) => <Link key={g} to={`/explore?q=${encodeURIComponent(g)}`} className="rounded-full bg-secondary px-3 py-1 text-xs">#{g}</Link>)}</div>
        <div className="card-surface mt-8 flex items-center gap-4 p-5">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-primary font-bold text-white">{t.seller_name[0]}</div>
          <div className="flex-1"><p className="font-semibold">{t.seller_name}</p><p className="text-xs text-muted-foreground">{t.seller.bio || "Designer on GrafixMarket"} · {t.seller.templates} templates</p></div>
          <Calendar size={14} className="text-muted-foreground" /><span className="text-xs text-muted-foreground">Since {fmtDate(t.seller.created_at)}</span>
        </div>
      </section>
      <Reviews t={t} reload={load} />
    </div>
  );
}
